import { Field, Textarea, Button, Accordion, AccordionItem, AccordionHeader, AccordionPanel } from "@fluentui/react-components";
import { FieldProps, ObjectFieldTemplatePropertyType, ObjectFieldTemplateProps, WidgetProps, getUiOptions } from "@rjsf/utils";
import { RJSFSchema } from "@rjsf/utils/lib/types";
import { QuestionCircleRegular, ChevronDownRegular, ChevronUpRegular, WarningRegular } from "@fluentui/react-icons";

import dynamic from 'next/dynamic';
const BundledEditor = dynamic(
    () => import('./bundle').then(module => module.BundledEditor),
    { ssr: false },
);

import { registerInputControlDesignerField } from "@eavfw/quickform-designer";
import { useRef, useState } from "react";
import { Editor, IAllProps } from '@tinymce/tinymce-react';
import { type Editor as TinyMCEEditor } from 'tinymce'

const ExpressionHelp: React.FC = () => {
    const [openItems, setOpenItems] = useState<string[]>([]);
    const [helpExpanded, setHelpExpanded] = useState(false);

    const handleToggle = (event: any, data: { value: string; openItems: string[] }) => {
        setOpenItems(data.openItems);
    };

    const codeBlockStyle = {
        backgroundColor: '#f6f8fa',
        padding: '8px',
        borderRadius: '4px',
        display: 'block' as const,
        fontFamily: 'Monaco, Consolas, monospace',
        marginBottom: '4px',
        fontSize: '12px',
        lineHeight: '1.4'
    };

    const inlineCodeStyle = {
        backgroundColor: '#e8e8e8',
        padding: '2px 4px',
        borderRadius: '3px',
        fontFamily: 'Monaco, Consolas, monospace'
    };

    const exampleDividerStyle = {
        borderTop: '1px solid #e1e4e8',
        marginTop: '16px',
        paddingTop: '12px'
    };

    const descriptionStyle = {
        color: '#666',
        fontSize: '12px',
        marginBottom: '8px'
    };

    const warningStyle = {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        padding: '8px',
        marginBottom: '12px',
        fontSize: '12px',
        color: '#856404'
    };

    return (
        <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e1e4e8',
            borderRadius: '4px',
            fontSize: '14px'
        }}>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: helpExpanded ? '8px' : '0px',
                    fontWeight: '600',
                    cursor: 'pointer'
                }}
                onClick={() => setHelpExpanded(!helpExpanded)}
            >
                <QuestionCircleRegular style={{ marginRight: '6px', color: '#0078d4' }} />
                Brug af udtryk (expressions)
                {helpExpanded ? <ChevronUpRegular style={{ marginLeft: 'auto' }} /> : <ChevronDownRegular style={{ marginLeft: 'auto' }} />}
            </div>

            {helpExpanded && (
                <>
                    <p style={{ margin: '0 0 12px 0', color: '#616161' }}>
                        Du kan bruge dynamiske udtryk i teksten ved at skrive <code style={inlineCodeStyle}>@{'{...}'}</code>
                    </p>

                    <Accordion
                        collapsible
                        multiple
                        openItems={openItems}
                        onToggle={handleToggle}
                    >
                        <AccordionItem value="expressions">
                            <AccordionHeader>Tilgængelige udtryk</AccordionHeader>
                            <AccordionPanel>
                                <div style={{ fontSize: '13px' }}>
                                    {/*<div style={{ marginBottom: '12px' }}>*/}
                                    {/*    <strong>@{'{variables(\'\<navn\>\')}'}</strong><br />*/}
                                    {/*    <span style={{ color: '#666' }}>Hent variabler defineret i formularen</span>*/}
                                    {/*</div>*/}

                                    {/*<div style={{ marginBottom: '12px' }}>*/}
                                    {/*    <strong>@{'{pretty(<værdi>)}'}</strong><br />*/}
                                    {/*    <span style={{ color: '#666' }}>Formatér værdi pænt (f.eks. datoer på dansk)</span>*/}
                                    {/*</div>*/}

                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>@{'{quotation().<variabelnavn>}'}</strong><br />
                                        <span style={{ color: '#666' }}>Tilgå overordnet tilbudsdata på beregner som totalpris, pris eksl. moms, navn <br />
                                            Tilgængelig data (variabelnavne) inkluderer: <code style={inlineCodeStyle}>priceamount, taxamount, totalpriceamount, quotationform.name</code>
                                        </span>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>@{'{formatNumber(<værdi>)}'}</strong><br />
                                        <span style={{ color: '#666' }}>Formater en værdi, fx dansk valuta</span>
                                    </div>

                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>@{'{answers().<spørgsmåls-logiske-navn>}'}</strong><br />
                                        <span style={{ color: '#666' }}>eller hvis spørgsmåls logiske navn indeholder specialtegn, som fx '?'</span><br />
                                        <strong>@{'{answers()[\'<spørgsmåls-logiske-navn>\']}'}</strong><br />
                                        <span style={{ color: '#666' }}>Hent brugerens svar til spørgsmålet fra formularen.
                                            <br />"spørgsmåls-logiske-navn" kan findes under "Source view" under for hvert spørgsmål under "Questions"-&gt;"&lt;spørsmålsnavn&gt;"-&gt;"logicalname".
                                        </span>
                                    </div>

                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>@{'{line(\'<produkt-option-navn>\').<variabelnavn>}'}</strong><br />
                                        <span style={{ color: '#666' }}>Hent prisudregninger eller information som indgår i tilbuddet ud for hver produkt-option.<br />
                                            "product-option-navn" kan findes enten under Spørgsmålets "Option" felt, eller i "Source view" under "Questions"-&gt;"&lt;spørsmålsnavn&gt;"-&gt;"productOption".
                                            <br />Find specifikke linjer i tilbuddet, dette inkluderer pris som hvert spørgsmål udgør af det samlede. <br />
                                            Tilgængelig data (variabelnavne) inkluderer: <code style={inlineCodeStyle}>line, priceamount, quantity, taxamount, totalpriceamount, unitprice</code>
                                        </span>
                                    </div>

                                    <div style={warningStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                            <WarningRegular style={{ marginRight: '4px', fontSize: '14px' }} />
                                            <strong>Bemærk</strong>
                                        </div>
                                        <span>Line udtryk understøtter endnu ikke "Product Collection" og "Product Picker" spørgsmålstyper fuldt ud. Implementation mangler for disse typer.</span>
                                    </div>

                                    {/*<div style={{ marginBottom: '12px' }}>*/}
                                    {/*    <strong>@{'{link()}'}</strong><br />*/}
                                    {/*    <span style={{ color: '#666' }}>Generér link til kundeområdet</span>*/}
                                    {/*</div>*/}
                                </div>
                            </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem value="examples">
                            <AccordionHeader>Eksempler</AccordionHeader>
                            <AccordionPanel>
                                <div style={{ fontSize: '13px' }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <code style={codeBlockStyle}>
                                            Som hustype har du valgt: @{'{answers().hustype}'}
                                        </code>
                                        <span style={descriptionStyle}>Som bliver:</span>
                                        <code style={codeBlockStyle}>
                                            Som hustype har du valgt: Bolig
                                        </code>
                                        <span style={descriptionStyle}>Udskifter brugerens @{'{answers().hustype}'} med hvad brugeren har valgt som spørgsmålet om hustype, fx. Bolig</span>
                                    </div>

                                    <div style={{ ...exampleDividerStyle, marginBottom: '8px' }}>
                                        <code style={codeBlockStyle}>
                                            Total pris: @{'{formatNumber(quotation().totalpriceamount,\'C\',\'da-dk\')'}.
                                        </code>
                                        <span style={descriptionStyle}>Viser den samlede pris formateret som dansk valuta</span>
                                    </div>

                                    <div style={exampleDividerStyle}>
                                        <code style={codeBlockStyle}>
                                            Hustype: @{'{answers().hustype}'} ekskl. moms | @{'{formatNumber(line(\'hustype\').priceamount,\'C\',\'da-dk\')}'}.
                                        </code>
                                        <span style={descriptionStyle}>Som fx kunne blive:</span>
                                        <code style={codeBlockStyle}>
                                            Hustype: bolig ekskl. moms | 122.400,00 kr.
                                        </code>
                                    </div>
                                </div>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </>)}
        </div>
     
    );
};

export const RichTextField: React.FC<FieldProps> = ({ uiSchema = {}, schema: rawSchema, required, formData, registry, title, name, ...props }) => {

    const { fields, formContext, schemaUtils, translateString, globalUiOptions } = registry;
    const schema = schemaUtils.retrieveSchema(rawSchema, formData);


    const uiOptions = getUiOptions(uiSchema, globalUiOptions);
    const editorRef = useRef<TinyMCEEditor | null>(null);
    const templateTitle = uiOptions.title ?? schema.title ?? title ?? name;
    const description = uiOptions.description ?? schema.description;

    const showExpressionHelp = true;

    return (<>
        <Field label={uiOptions.label === false ? '' : templateTitle} required={required} hint={description} >

            <BundledEditor
                onInit={(_evt, editor) => editorRef.current = editor}
                onEditorChange={(content) => props.onChange(content)}
                value={formData}

                init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                        'advlist', 'anchor', 'autolink', 'help', 'image', 'link', 'lists',
                        'searchreplace', 'table', 'wordcount'
                    ],
                    style_formats: [
                        {
                            title: 'Prisside', items: [
                                {
                                    title: 'Prisside - Headline',
                                    inline: 'span',
                                    styles: { 'font-size': '35px', 'font-weight': 'normal' }
                                },
                                {
                                    title: 'Prisside - Pris',
                                    inline: 'span',
                                    styles: { 'font-size': '40px', 'font-weight': '900' }
                                },
                                {
                                    title: 'Prisside - Moms',
                                    inline: 'span',
                                    styles: { 'font-size': '35px', 'font-weight': 'normal' }
                                },
                                {
                                    title: 'Prisside - Subline',
                                    inline: 'span',
                                    styles: { 'font-size': '25px', 'font-weight': 'bold' }
                                },
                                {
                                    title: 'Prisside - Brødtekst',
                                    inline: 'span',
                                    styles: { 'font-size': '20px', 'font-weight': 'normal' }
                                }
                            ]
                        }
                    ],
                    toolbar: 'undo redo | styles | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | anchor |' +
                        'table | removeformat | help',
                    content_style: 'strong { font-weight: 900}',
                }}
            />

            {showExpressionHelp && <ExpressionHelp />}
        </Field>

    </>);
};

registerInputControlDesignerField("QF_EndingSlideTextField", RichTextField);
registerInputControlDesignerField("QF_EndingSlideParagraphField", RichTextField);
